import { ReactEditor } from 'slate-react';
import { Transforms } from 'slate';
import { getElementsByAttribute, getElementsByType, checkIfItHasSuperscript, getNewContentPosition } from '../../Utils/SlateUtils.js';
import { ptsToPxls, pxlsToPts } from '../pageLayoutMath';
import { computeNextMaxHeight } from '../pageBreakMath';
import { collectFootnoteData, buildSuperscriptsObject } from './collectFootnoteData';
import { detectAnomalies } from './detectAnomalies';
import { selectFootnoteScenario } from './selectScenario';

/**
 * Core footnote layout engine V2 — fixes three bugs in pageHeightInPxs calculation:
 *
 * Bug 1: Off-by-one — was using boundaries[length-2] instead of boundaries[length-1]
 * Bug 2: isLastDocNode branch omitted + heightToUse, giving the start of the page not the bottom
 * Bug 3: manualPageBreak within the current page was not shortening the effective page height
 *
 * @param {object}      editor           - Slate editor instance (this.state.editor)
 * @param {HTMLElement} element          - outer div DOM node (divRef.current)
 * @param {object}      state            - component state snapshot at callback time
 * @param {object}      props            - component props
 * @param {Array|null}  passedBoundaries - pre-computed boundaries forwarded by recursive calls
 * @param {object}      callbacks        - { setState, onChange, heightChange }
 */
export function runFootnoteOrchestrator(editor, element, state, props, passedBoundaries, callbacks) {
	if (!element) return;

	const { setState, onChange, heightChange } = callbacks;

	// Build elements array from the Slate model so image-wrappers are included even
	// when ImageWrapperElement renders an extra outer div (contentEditable=false) that
	// does not carry data-slate-node=element. Walk up from the Slate element to find
	// the true direct child of the value div so height measurements are correct.
	const slateValueEl = element.querySelector('[data-slate-node=value]');
	const elements = slateValueEl
		? editor.children.map(child => {
			try {
				let node = ReactEditor.toDOMNode(editor, child);
				while (node.parentElement && node.parentElement !== slateValueEl) {
					node = node.parentElement;
				}
				return node;
			} catch (e) {
				return null;
			}
		})
		: [];
	const styles = props.styles;
	const flowStyles = props.flowingConfig.styles;

	// STEP ONE: Get the base height of the page
	const margins = props.flowingConfig.margins;
	const firstPageMargins = props.flowingConfig.firstPageMargins;
	const lastPageMargins = props.flowingConfig.lastPageMargins;
	const size = props.pageSize;
	const {
		// STEP ONE — base heights
		basePageHeightInPts,
		baseFirstPageHeightInPts,
		baseLastPageHeightInPts,
		baseFirstAndLastPageHeightInPts,
		// STEP TWO — change-tracking arrays
		newFootnoteWrappersCreated, deletedFootnoteWrappers,
		listsItemsToDelete, listsItemsToAdd, newListsNodes,
		hasRelocatedFootnoteContent, fnwToBeMoved, fnwInTheWrongPage,
		// STEP THREE — Slate queries + pre-built lookups
		allFootnoteWrappers, allFootnoteContent, allSuperscripts,
		fnWLists, footnoteContentObject, superscriptsObject,
	} = collectFootnoteData({
		editor, margins, size, firstPageMargins, lastPageMargins,
		styles, flowStyles,
		toDOMHeight: (ed, node) => {
			const el = ReactEditor.toDOMNode(ed, node);
			return el ? el.getBoundingClientRect().height : 0;
		},
	});

	let maxHeight = 0;
	let actualHeight = 0;

	let updatedBoundaries = passedBoundaries;
	let hasFootnotesInManualBreaks = false;
	let finished = false;

	let boundaries = [];
	const addedManuals = [];
	const manualBoundaries = state.manualPageBreaks;

	function getMaxHeight(height, index, isList = false) {
		return computeNextMaxHeight({
			height, index, isList, maxHeight,
			boundaries, addedManuals, manualBoundaries,
			basePageHeightInPts,
			baseFirstPageHeightInPts,
			baseLastPageHeightInPts,
			baseFirstAndLastPageHeightInPts,
			firstPageMargins, lastPageMargins,
			editorChildren: editor.children,
		});
	}

	// STEP FOUR: Iterate through all elements in the document to calculate their heights and identify footnotes
	elements.forEach((el, index) => {
		if (!el) return;
		const currentHeight = actualHeight;
		const elHeight = el.getBoundingClientRect().height;
		const elMb = parseFloat(window.getComputedStyle(el).getPropertyValue('margin-bottom'), 10);
		const elTotHeight = elHeight + elMb;
		actualHeight += elTotHeight;

		if (el.nodeName === 'UL' || el.nodeName === 'OL') {
			const listParentMarginBottom = parseFloat(window.getComputedStyle(el).getPropertyValue('margin-bottom'), 10);
			if (el.children.length > 0 && listParentMarginBottom === 0) {
				actualHeight += parseInt(window.getComputedStyle(el.children[0]).getPropertyValue('margin-bottom'), 10);
			}
			if (actualHeight > maxHeight) {
				maxHeight = getMaxHeight(actualHeight, index, true);
			}

		} else if (el.nodeName === 'DIV' && el.className === 'table-wrapper-outer') {
			const actualTable = el.children[0]?.children[1] || el.children[0] || el;
			const tableHeight = actualTable.getBoundingClientRect().height;
			const tableMarginBottom = parseFloat(window.getComputedStyle(actualTable).getPropertyValue('margin-bottom'), 10);
			const newActualHeight = currentHeight + tableHeight + tableMarginBottom;
			actualHeight = newActualHeight;
			if (actualHeight > maxHeight) maxHeight = getMaxHeight(actualHeight, index);

		} else if (el.nodeName === 'DIV' && el.className === 'footnote-wrapper') {
			const isLastDocNode = index === editor.children.length - 1;

			let _pageHeightPts;
			if (boundaries.length === 0 && firstPageMargins && lastPageMargins) _pageHeightPts = baseFirstAndLastPageHeightInPts;
			else if (boundaries.length === 0 && firstPageMargins) _pageHeightPts = baseFirstPageHeightInPts;
			else if ((boundaries.length === 0 || isLastDocNode) && lastPageMargins) _pageHeightPts = baseLastPageHeightInPts;
			else _pageHeightPts = basePageHeightInPts;
			const heightToUse = ptsToPxls(_pageHeightPts);

			// boundaries[length-1].pos = cumulative pts to the END of the current page.
			// (This boundary was just pushed when the current page started; no 'at' yet.)
			// Using lastBoundary (captured before any isLastDocNode pop) gives the correct
			// page bottom for all cases, including isLastDocNode on pages 3+ and manual breaks.
			const lastBoundary = boundaries[boundaries.length - 1];

			const prePoppedBoundaries = isLastDocNode ? [...boundaries] : boundaries;
			if (isLastDocNode) boundaries.pop();

			//const pageHeightInPxs = lastBoundary ? ptsToPxls(lastBoundary.pos) : heightToUse;
				const pageHeightInPxs = boundaries.length > 1
				? heightToUse + ptsToPxls(boundaries[boundaries.length - 2].pos)
				: isLastDocNode && lastBoundary
					? ptsToPxls(lastBoundary.pos)
					: heightToUse;

			const currentBoundaryIndex = boundaries.findIndex(b => b.at && b.at[0] && b.at[0] >= index);

			let fnwIndex = fnWLists.findIndex(fnw => fnw.at[0] === index);

			const styleName = flowStyles['footnoteContent'];
			const fnContentStyle = styles[styleName] ? styles[styleName] : {};
			const addedMarginBottom = ptsToPxls(fnContentStyle.paddingBelowParagraph);
			const defaultMarginTop = +ptsToPxls(10).toFixed(2);
			const fnwNode = editor.children[index];

			const contentWithNoSource = fnwNode.children.filter(child => {
				const foundSource = superscriptsObject[child.superscriptNumber];
				return foundSource ? false : true;
			}) || [];

			const heightToSubstract = contentWithNoSource.length !== 0 ? contentWithNoSource.reduce((acc, curr) => {
				const foundContent = footnoteContentObject[curr.superscriptNumber];
				const contentElement = foundContent ? ReactEditor.toDOMNode(editor, editor.children[foundContent.at[0]].children[foundContent.at[1]]) : null;
				const contentHeight = contentElement ? contentElement.getBoundingClientRect().height : 0;
				return contentElement ? acc + contentHeight : acc;
			}, 0) : 0;

			const footNoteWrapperHeight = elHeight + addedMarginBottom - heightToSubstract;

			const elBefore = elements[index - 1] || null;
			const elBeforeHeight = elBefore ? elBefore.getBoundingClientRect().height : 0;
			const elBeforeMarginBottom = elBefore ? parseFloat(window.getComputedStyle(elBefore).getPropertyValue('margin-bottom'), 10) : 0;

			// STEP FIVE: Find data anomalies
			const availableSpaceForFNW = pageHeightInPxs - currentHeight + elBeforeMarginBottom;
			const availableHeightAfterFNW = availableSpaceForFNW - footNoteWrapperHeight;
			const nextNode = editor.children[index + 1] || null;
			const nextElement = elements[index + 1] || null;
			const lastElementList = nextNode && (nextNode.type === 'bulleted-list' || nextNode.type === 'numbered-list') && nextNode.children && nextNode.children.length > 0 ? ReactEditor.toDOMNode(editor, nextNode.children[nextNode.children.length - 1]) : null;
			const lastListItemMarginBottom = lastElementList ? parseFloat(window.getComputedStyle(lastElementList).getPropertyValue('margin-bottom'), 10) : 0;
			const nextElementMarginBottom = nextElement ? parseFloat(window.getComputedStyle(nextElement).getPropertyValue('margin-bottom'), 10) : 0;
			const plainNextElementHeight = nextElement ? nextElement.getBoundingClientRect().height : 0;
			const {
				maxIterationsReached, tempContentToDelete, deletedObject,
				sourceIsBeforeManual, isManual, sourceIsAtDifferentBoundary,
				spaceBeforeList, hasAnomalies,
			} = detectAnomalies({
				fnwIndex, index, boundaries: prePoppedBoundaries,
				fnWLists, footnoteContentObject, superscriptsObject, allFootnoteWrappers,
				deletedFootnoteWrappers, contentWithNoSource,
				shallowOnChangeCount: state.shallowOnChangeCount,
				isLastDocNode,
				currentHeight, footNoteWrapperHeight, elBeforeMarginBottom, maxHeight, pageHeightInPxs,
				plainNextElementHeight, nextElementMarginBottom, defaultMarginInPx: defaultMarginTop,
				hasListAfterWrapper: !!lastElementList,
				editor,
			});
			let listNeedModifications = false;

			// STEP FIVE part 2 — deal with ANOMALIES:
			// (1) shallowOnChangeCount has reached max — stuck in a loop
			if (maxIterationsReached) {
				const currentMarginTop = editor.children[index].marginTop ? editor.children[index].marginTop : 10;
				const newActualHeight = currentHeight + footNoteWrapperHeight + (currentMarginTop === 10 ? +ptsToPxls(currentMarginTop).toFixed(2) : currentMarginTop) - (elBefore ? elBeforeMarginBottom : 0);
				actualHeight = newActualHeight;
			}
			// (2) footnote wrapper is empty and has no content
			if (fnwIndex !== null && fnWLists[fnwIndex] && fnWLists[fnwIndex].content && ((!deletedObject[index] && fnWLists[fnwIndex].content.length === 0) || (contentWithNoSource.length > 0 && contentWithNoSource.length === fnWLists[fnwIndex].content.length))) {
				deletedFootnoteWrappers.push({ at: index, delete: true });
				deletedObject[index] = { at: index, delete: true };
			}
			// (still anomaly 2) footnote-wrapper deleted due to changes in a different fnw
			if (deletedObject[index]) {
				actualHeight = currentHeight;
			}
			// (3) source superscript is before a manual pageBreak, fnw is after
			if (sourceIsBeforeManual.length > 0) {
				const nextNodeIndex = boundaries[currentBoundaryIndex - 1] && boundaries[currentBoundaryIndex - 1].at && boundaries[currentBoundaryIndex - 1].at[0] - 1;
				const nodeAtEndOfPageWidthManual = nextNodeIndex && editor.children[nextNodeIndex];
				const relevantFNW = fnWLists.find(fnw => fnw.at[0] === nextNodeIndex);

				if (nodeAtEndOfPageWidthManual && nodeAtEndOfPageWidthManual.type === 'footnote-wrapper') {
					fnwToBeMoved.push({
						at: [nextNodeIndex],
						to: [nextNodeIndex],
						element: { ...editor.children[index] },
						contentToAdd: sourceIsBeforeManual.map(superscriptNumber => {
							const extraFootnoteContent = { ...superscriptsObject[superscriptNumber], newContentPath: [nextNodeIndex, getNewContentPosition(relevantFNW.content, +superscriptNumber)] };
							return extraFootnoteContent;
						}),
						contentToDelete: tempContentToDelete
					});

					if (editor.children[index].children.length === 1) {
						deletedFootnoteWrappers.push({ at: index, delete: true });
					} else {
						const contentToBeDeleted = sourceIsBeforeManual.map(superscriptNumber => {
							return superscriptsObject[superscriptNumber] && superscriptsObject[superscriptNumber].contentPath && superscriptsObject[superscriptNumber].contentPath[1];
						});
						fnwToBeMoved.push({
							at: [index],
							to: [index],
							element: { ...editor.children[index] },
							contentToDelete: tempContentToDelete.length > 0 ? [...contentToBeDeleted, ...tempContentToDelete] : contentToBeDeleted
						});
					}
					const newActualHeight = currentHeight + footNoteWrapperHeight + (elBeforeMarginBottom > defaultMarginTop ? elBeforeMarginBottom : defaultMarginTop - elBeforeMarginBottom);
					actualHeight = newActualHeight;

				} else if (nextNodeIndex && editor.children[nextNodeIndex]) {
					if (editor.children[index].children.length === 1) {
						fnwToBeMoved.push({
							at: [index],
							to: [nextNodeIndex],
							element: { ...editor.children[index] },
						});
					} else {
						fnwToBeMoved.push({
							at: [nextNodeIndex],
							to: [nextNodeIndex],
							element: { ...editor.children[index] },
							contentToAdd: sourceIsBeforeManual.map(superscriptNumber => {
								const extraFootnoteContent = { ...superscriptsObject[superscriptNumber], newContentPath: [nextNodeIndex, getNewContentPosition(relevantFNW.content, +superscriptNumber)] };
								return extraFootnoteContent;
							})
						});
						fnwToBeMoved.push({
							at: [index],
							to: [index],
							element: { ...editor.children[index] },
							contentToDelete: sourceIsBeforeManual.map(superscriptNumber => {
								return superscriptsObject[superscriptNumber] && superscriptsObject[superscriptNumber].contentPath && superscriptsObject[superscriptNumber].contentPath[1];
							})
						});
					}
					const newActualHeight = currentHeight + footNoteWrapperHeight + (elBeforeMarginBottom > defaultMarginTop ? elBeforeMarginBottom : defaultMarginTop - elBeforeMarginBottom);
					actualHeight = newActualHeight;
				}
			}
			// (4) footnote-wrapper is just before a manual page break
			if (isManual) {
				const nextNode = editor.children[index + 1];
				const addNextElementHeight = nextNode ? (nextNode.pageBreaks && nextNode.pageBreaks.length === 0) || !nextNode.pageBreaks : false;
				const nextElement = ReactEditor.toDOMNode(editor, nextNode);
				const nextElementHeight = addNextElementHeight ? nextElement.getBoundingClientRect().height : 0;
				const nextElementMarginBottom = addNextElementHeight ? parseFloat(window.getComputedStyle(nextElement).getPropertyValue('margin-bottom'), 10) : 0;
				const minMarginInPx = +ptsToPxls(10).toFixed(2);
				const marginToSubtract = addNextElementHeight && nextElementMarginBottom > minMarginInPx ? nextElementMarginBottom : minMarginInPx;
				const marginTopInPts = addNextElementHeight ? +pxlsToPts(pageHeightInPxs - currentHeight - nextElementHeight - footNoteWrapperHeight - marginToSubtract).toFixed(2) : +pxlsToPts(pageHeightInPxs - currentHeight - footNoteWrapperHeight + elBeforeMarginBottom).toFixed(2);
				const currentMarginTop = editor.children[index].marginTop ? editor.children[index].marginTop : 0;

				if (currentMarginTop !== marginTopInPts) {
					fnwToBeMoved.push({
						at: [index],
						to: [addNextElementHeight ? index + 1 : index],
						element: { ...editor.children[index], marginTop: marginTopInPts },
						contentToDelete: tempContentToDelete
					});
				}

				hasFootnotesInManualBreaks = true;
				const newActualHeight = pageHeightInPxs;
				actualHeight = newActualHeight;
			}
			// (5) source superscript is at different boundary
			if (sourceIsAtDifferentBoundary.length > 0) {
				fnwInTheWrongPage.push(...sourceIsAtDifferentBoundary);
				const newActualHeight = currentHeight + footNoteWrapperHeight + (elBeforeMarginBottom > defaultMarginTop ? elBeforeMarginBottom : defaultMarginTop - elBeforeMarginBottom);
				actualHeight = newActualHeight;
			}
			// (6) next element after fnw is a list node
			if (spaceBeforeList) {
				let node1 = [];
				let node2 = [];
				let endOfMap = false;
				let tempDeletedListItems = [];
				const roundedMinMarginInPxs = +ptsToPxls(10).toFixed(2);

				nextNode.children.map((litsItem, listItemIndex) => {
					const listItemElement = ReactEditor.toDOMNode(editor, litsItem);
					const listItemHeight = +listItemElement.getBoundingClientRect().height;
					const listItemMarginBottom = parseFloat(window.getComputedStyle(listItemElement).getPropertyValue('margin-bottom'), 10);
					const listItemTotalHeight = listItemHeight + listItemMarginBottom;
					const updatedSpaceForFNW = availableHeightAfterFNW - elBeforeMarginBottom - (listItemTotalHeight * listItemIndex) - listItemHeight;

					if (updatedSpaceForFNW > roundedMinMarginInPxs) {
						node1.push(litsItem);
						if (listItemIndex === nextNode.children.length - 1) {
							endOfMap = true;
						}
					} else {
						tempDeletedListItems.push({ at: [index + 1, listItemIndex] });
						node2.push(litsItem);
						if (listItemIndex === nextNode.children.length - 1) {
							endOfMap = true;
						}
					}
				});

				if (endOfMap && node2.length > 0) {
					const nodeAfterFNWNewLocation = editor.children[index + 2] ? editor.children[index + 2] : null;
					const newListNode = nodeAfterFNWNewLocation.type !== 'bulleted-list' && nodeAfterFNWNewLocation.type !== 'numbered-list' ? { newList: true, content: { ...nextNode, children: node2 }, at: [index + 2] } : null;

					if (newListNode && node2.length > 0 && node2.length < nextNode.children.length) {
						newListsNodes.push(newListNode);
					} else if (node2.length > 0 && node2.length < nextNode.children.length) {
						listsItemsToAdd.push({ at: [index + 2], contentToAdd: node2 });
					}

					if (node2.length > 0 && node2.length < nextNode.children.length) {
						if (tempDeletedListItems.length > 0) listsItemsToDelete.push(...tempDeletedListItems);

						fnwToBeMoved.push({
							at: [index],
							to: [index + 1],
							element: { ...editor.children[index], marginTop: 10 },
							contentToDelete: tempContentToDelete
						});

						listNeedModifications = true;
						const newActualHeight = currentHeight + footNoteWrapperHeight + (nextElementMarginBottom > roundedMinMarginInPxs ? 0 : roundedMinMarginInPxs - nextElementMarginBottom) + (newListNode ? lastListItemMarginBottom : 0);
						actualHeight = newActualHeight;
					}
				}
			}

			// STEP SIX - NORMAL SCENARIOS
			if (!hasAnomalies && listNeedModifications === false) {
				const hasSuperscriptAfter = checkIfItHasSuperscript(editor, index + 1);
				const hasSuperscriptBefore = checkIfItHasSuperscript(editor, index - 1);
				const elBeforePrevIndex = elements[index - 2] || null;
				const elBeforePrevIndexMarginBottom = elBeforePrevIndex ? parseFloat(window.getComputedStyle(elBeforePrevIndex).getPropertyValue('margin-bottom'), 10) : 0;
				const scenarioDesc = selectFootnoteScenario({
					fnwNode, index, editorChildren: editor.children, isLastDocNode,
					currentHeight, footNoteWrapperHeight,
					elBeforeHeight, elBeforeMarginBottom, elBeforePrevIndexMarginBottom,
					availableHeightAfterFNW, availableSpaceForFNW,
					pageHeightInPxs, heightToUse, defaultMarginTopInPxs: defaultMarginTop,
					nextElementHeight: plainNextElementHeight,
					nextElementBottomMargin: nextElementMarginBottom,
					fnWLists, superscriptsObject,
					fnwToBeMoved, newFootnoteWrappersCreated, hasRelocatedFootnoteContent,
					hasSuperscriptAfter, hasSuperscriptBefore,
					tempContentToDelete, currentBoundaryIndex, boundaries,
				});
				scenarioDesc.fnwToMove.forEach(e => fnwToBeMoved.push(e));
				if (scenarioDesc.fnwToUpdate) fnwToBeMoved[scenarioDesc.fnwToUpdate.index] = scenarioDesc.fnwToUpdate.item;
				scenarioDesc.fnwToDelete.forEach(e => deletedFootnoteWrappers.push(e));
				scenarioDesc.newFnwToAdd.forEach(e => newFootnoteWrappersCreated.push(e));
				if (scenarioDesc.newFnwToUpdate) newFootnoteWrappersCreated[scenarioDesc.newFnwToUpdate.index] = scenarioDesc.newFnwToUpdate.item;
				if (scenarioDesc.relocatedContent) hasRelocatedFootnoteContent[scenarioDesc.relocatedContent.key] = scenarioDesc.relocatedContent.value;
				if (scenarioDesc.newBoundaryAt) boundaries[currentBoundaryIndex] = { ...boundaries[currentBoundaryIndex], at: scenarioDesc.newBoundaryAt };
				if (scenarioDesc.setUpdatedBoundaries) updatedBoundaries = boundaries;
				actualHeight = scenarioDesc.newActualHeight;
			}
		} else {
			if (actualHeight > maxHeight && index !== editor.children.length - 1) {
				maxHeight = getMaxHeight(actualHeight, index);
			}
		}

		if (index === elements.length - 1) {
			finished = true;
		}
	});

	// STEP SEVEN — APPLY CHANGES
	const MAX_SHALLOW_ITERATIONS = 6;
	if ((fnwToBeMoved.length > 0 || newFootnoteWrappersCreated.length > 0 || deletedFootnoteWrappers.length > 0 || newListsNodes.length > 0 || listsItemsToAdd.length > 0 || fnwInTheWrongPage.length > 0) && finished) {
		// Guard: prevent infinite loop — same check as detectAnomalies.maxIterationsReached
		if (state.shallowOnChangeCount >= MAX_SHALLOW_ITERATIONS) {
			setState({ shallowOnChangeCount: 0 });
			return;
		}

		const createdOrDeletedFNW = newFootnoteWrappersCreated.concat(deletedFootnoteWrappers).sort((a, b) => b.at - a.at);

		if (listsItemsToDelete.length > 0) {
			listsItemsToDelete.sort((a, b) => a.at[0] !== b.at[0] ? b.at[0] - a.at[0] : b.at[1] - a.at[1]).forEach(item => {
				Transforms.removeNodes(editor, { at: item.at });
			});
		}

		if (listsItemsToAdd.length > 0) {
			listsItemsToAdd.sort((a, b) => b.at[0] - a.at[0]).forEach((item, idx) => {
				const affectedList = editor.children[item.at[0]];
				const updatedChildren = [...item.contentToAdd, ...affectedList.children];
				Transforms.insertNodes(editor, { ...affectedList, children: updatedChildren }, { at: [item.at[0]] });
				Transforms.removeNodes(editor, { at: [item.at[0] + 1] });
			});
		}

		if (fnwToBeMoved.length > 0) {
			fnwToBeMoved.forEach((item, index) => {
				Transforms.setNodes(editor, { marginTop: item.element.marginTop }, { at: item.at });

				if (item.contentToDelete && item.contentToDelete.length > 0) {
					[...item.contentToDelete].sort((a, b) => b - a).forEach(contentItem => {
						Transforms.removeNodes(editor, { at: [item.at[0], contentItem] });
					});
				}

				if (item.contentToAdd && item.contentToAdd.length > 0) {
					item.contentToAdd.forEach(contentItem => {
						Transforms.insertNodes(editor, contentItem.content, { at: [item.at[0], contentItem.newContentPath[1]] });
					});
				}

				if (item.at && item.at[0] !== item.to[0]) {
					Transforms.moveNodes(editor, { at: item.at, to: item.to });
				}

				if (index === fnwToBeMoved.length - 1 && createdOrDeletedFNW.length === 0 && newListsNodes.length === 0 && fnwInTheWrongPage.length === 0) {
					setState({ shallowOnChangeCount: state.shallowOnChangeCount + 1 }, () => {
						onChange(editor.children, false, updatedBoundaries);
					});
				}
			});
		}

		if (createdOrDeletedFNW.length > 0) {
			createdOrDeletedFNW.sort((a, b) => b.at - a.at).forEach((fnw, idx) => {
				if (fnw.delete) {
					Transforms.removeNodes(editor, { at: [fnw.at] });
				} else {
					Transforms.insertNodes(editor, fnw.content, { at: [fnw.at] });
				}
				if (idx === createdOrDeletedFNW.length - 1 && newListsNodes.length === 0 && fnwInTheWrongPage.length === 0) {
					setState({ shallowOnChangeCount: state.shallowOnChangeCount + 1 }, () => {
						onChange(editor.children, false, updatedBoundaries);
					});
				}
			});
		}

		if (newListsNodes.length > 0) {
			newListsNodes.sort((a, b) => b.at[0] - a.at[0]).forEach((list, idx) => {
				Transforms.insertNodes(editor, list.content, { at: [list.at[0]] });
				if (idx === newListsNodes.length - 1 && fnwInTheWrongPage.length === 0) {
					setState({ shallowOnChangeCount: state.shallowOnChangeCount + 1 }, () => {
						onChange(editor.children, false, updatedBoundaries);
					});
				}
			});
		}

		let createdFNWAfterRlocatingLostSuperscripts = [];
		let deletedNWAfterRlocatingLostSuperscripts = [];
		let finishedLostSuperscriptsRelocation = false;

		if (fnwInTheWrongPage.length > 0) {
			const updatedSupList = getElementsByAttribute(editor, 'superscript');
			const updatedFootnoteWrappersList = getElementsByType(editor, 'footnote-wrapper', false);
			const updatedFootnoteContentList = getElementsByType(editor, 'footnote-content', false);

			const fnWLists = updatedFootnoteWrappersList.map((fnw) => {
				return {
					at: fnw[1],
					content: fnw[0].children.map(fnc => fnc.superscriptNumber)
				};
			});

			const footnoteContentObject = updatedFootnoteContentList.reduce((acc, curr) => {
				return {
					...acc, [curr[0].superscriptNumber]: {
						at: curr[1],
						superscriptNumber: curr[0].superscriptNumber,
						contentText: curr[0].children,
					}
				};
			}, {});

			const superscriptsObject = buildSuperscriptsObject(
				updatedSupList,
				updatedFootnoteWrappersList,
				(node) => {
					const el = ReactEditor.toDOMNode(editor, node);
					return el ? el.getBoundingClientRect().height : 0;
				},
			);

			let fncToMoveFromOriginalFNW = [];

			fnwInTheWrongPage.sort((a, b) => b.sourceAt - a.sourceAt).forEach((fnc, idx) => {
				const supNumber = +fnc.superscriptNumber;
				const supString = fnc.superscriptNumber;
				const updatedSourceIndex = superscriptsObject[supString].at[0];
				const sourceBoundary = boundaries.find(b => b.at && b.at[0] >= updatedSourceIndex);
				const updatedFNCPath = footnoteContentObject[supString].at;
				const targetBoundary = boundaries.find(b => b.at && b.at[0] > updatedFNCPath[0]);
				// Helper — closes over per-iteration state; handles the shared fallback logic
				// for both prev-direction and next-direction FNW relocation.
				function relocateToFNW(adjacentFNW, getAdjacentDest) {
					const parentFNW = fnWLists.find(fnw => fnw.content.includes(supString));
					if (adjacentFNW && adjacentFNW.at && adjacentFNW.at[0] > updatedSourceIndex && adjacentFNW.at[0] < sourceBoundary.at[0]) {
						Transforms.moveNodes(editor, { at: [updatedFNCPath], to: getAdjacentDest(adjacentFNW) });
					} else {
						if (!targetBoundary && !sourceBoundary) {
							if (parentFNW && parentFNW.at && parentFNW.at[0] === editor.children.length - 1) {
								const prevIndex = parentFNW.at[0] - 1;
								if (prevIndex >= 0 && !checkIfItHasSuperscript(editor, prevIndex)) {
									Transforms.moveNodes(editor, { at: parentFNW.at, to: [prevIndex] });
								}
							}
						} else if (parentFNW && parentFNW.content.length === 1 && sourceBoundary && sourceBoundary.at) {
							Transforms.moveNodes(editor, { at: [updatedFNCPath[0]], to: [sourceBoundary.at[0] - 1] });
						} else {
							if (createdFNWAfterRlocatingLostSuperscripts.length > 0 && createdFNWAfterRlocatingLostSuperscripts[createdFNWAfterRlocatingLostSuperscripts.length - 1].at[0] === sourceBoundary.at[0] - 1) {
								const parentIndex = createdFNWAfterRlocatingLostSuperscripts.length - 1;
								const parent = createdFNWAfterRlocatingLostSuperscripts[parentIndex];
								createdFNWAfterRlocatingLostSuperscripts[parentIndex] = { ...parent, content: [...parent.content, supString], footnoteContentNodesList: [...parent.footnoteContentNodesList, fnc.footNoteNode] };
								if (createdFNWAfterRlocatingLostSuperscripts[parentIndex].content.length === parentFNW.content.length) {
									deletedNWAfterRlocatingLostSuperscripts.push({ at: parentFNW.at, delete: true });
								} else {
									fncToMoveFromOriginalFNW.push({ at: updatedFNCPath });
								}
							} else {
								createdFNWAfterRlocatingLostSuperscripts.push({ at: [sourceBoundary.at[0] - 1], content: [supString], footnoteContentNodesList: [fnc.footNoteNode] });
								fncToMoveFromOriginalFNW.push({ at: updatedFNCPath });
							}
						}
					}
				}

				if ((targetBoundary && sourceBoundary.at[0] !== targetBoundary.at[0]) || !targetBoundary) {
					if ((targetBoundary && targetBoundary.at[0] > sourceBoundary.at[0]) || !targetBoundary) {
						if (sourceBoundary && sourceBoundary.at[0] === updatedSourceIndex || (sourceBoundary && sourceBoundary.at[0] - 1 === updatedSourceIndex)) {
							Transforms.insertNodes(editor, { type: 'text', children: [{ text: '' }] }, { at: [updatedSourceIndex] });
						} else {
							const prevFNW = supNumber > 1 ? fnWLists.find(fnw => fnw.content.includes((supNumber - 1).toString()) && !fnw.content.includes(supString)) : createdFNWAfterRlocatingLostSuperscripts.length > 0 ? createdFNWAfterRlocatingLostSuperscripts.find(fnw => fnw.content.includes((supNumber - 1).toString()) && !fnw.content.includes(supString)) : null;
							relocateToFNW(prevFNW, (fnw) => [fnw.at[0], getNewContentPosition(fnw.content, supNumber)]);
						}
					} else if (updatedSourceIndex > updatedFNCPath[0]) {
						const nextFNW = footnoteContentObject[supNumber + 1] ? fnWLists.find(fnw => fnw.content.includes((supNumber + 1).toString()) && !fnw.content.includes(supString)) : null;
						relocateToFNW(nextFNW, (fnw) => [fnw.at[0], 0]);
					}
				}

				if (idx === fnwInTheWrongPage.length - 1) {
					finishedLostSuperscriptsRelocation = true;
					const sortedDeleting = fncToMoveFromOriginalFNW.sort((a, b) => b.at[0] !== a.at[0] ? b.at[0] - a.at[0] : b.at[1] - a.at[1]);
					sortedDeleting.forEach(item => Transforms.removeNodes(editor, { at: item.at }));

					if (createdFNWAfterRlocatingLostSuperscripts.length === 0 && deletedNWAfterRlocatingLostSuperscripts.length === 0) {
						setState({ shallowOnChangeCount: state.shallowOnChangeCount + 1 }, () => {
							onChange(editor.children, false, updatedBoundaries);
						});
					}
				}
			});
		}

		if (createdFNWAfterRlocatingLostSuperscripts.length > 0 || deletedNWAfterRlocatingLostSuperscripts.length > 0) {
			const newAndDeletedFNW = createdFNWAfterRlocatingLostSuperscripts.concat(deletedNWAfterRlocatingLostSuperscripts).sort((a, b) => b.at[0] - a.at[0]);

			newAndDeletedFNW.forEach((fnw, idx) => {
				if (fnw.delete) {
					Transforms.removeNodes(editor, { at: fnw.at });
				} else {
					const sortedChildren = fnw.footnoteContentNodesList.sort((a, b) => (+a.superscriptNumber) - (+b.superscriptNumber));
					Transforms.insertNodes(editor, { type: 'footnote-wrapper', children: sortedChildren }, { at: fnw.at });
				}
				if (idx === newAndDeletedFNW.length - 1) {
					setState({ shallowOnChangeCount: state.shallowOnChangeCount + 1 }, () => {
						onChange(editor.children, false, updatedBoundaries);
					});
				}
			});
		}

	} else if (finished) {
		const manuals = hasFootnotesInManualBreaks ? updatedBoundaries && updatedBoundaries.length > 0 && updatedBoundaries.filter(b => b.manual) : null;
		const roundedActualHeight = (+actualHeight.toFixed(4));

		setState({ shallowOnChangeCount: 0 }, () => {
			heightChange(roundedActualHeight, state, manuals);
		});
	}
}
